import { ApolloClient } from "@apollo/client";
import { DEFAULT_FLAGS, OrgId } from "@hex/common";
import { configureStore } from "@reduxjs/toolkit";
import createSagaMiddleware from "redux-saga";

import { GetHexFlagFn } from "../util/useHexFlags.js";

import { rootSaga } from "./sagas";
import { agentChatReducer } from "./slices/agentChatSlice";
import { agentLogicViewReducer } from "./slices/agentLogicViewSlice";
import { projectChatReducer } from "./slices/projectChatSlice";
import { appSessionEventSliceReducer } from "./slices/appSessionEventSlice.js";
import { appSessionMPReducer } from "./slices/appSessionMPSlice";
import { appViewReducer } from "./slices/appViewSlice.js";
import { browserStorageReducer } from "./slices/browserStorageSlice.js";
import { cellGraphReducer } from "./slices/cellGraphSlice";
import { contextWorkbenchReducer } from "./slices/contextWorkbenchSlice";
import { currentUserReducer } from "./slices/currentUserSlice.js";
import { dataAppReducer } from "./slices/dataAppSlice";
import { dataAppFileMPReducer } from "./slices/dataAppFileMPSlice";
import { dataBrowserTreeReducer } from "./slices/dataBrowserSlice.js";
import { hexMPReducer } from "./slices/hexMPSlice.js";
import { hexSelectionReducer } from "./slices/hexSelectionSlice.js";
import { hexVersionMPReducer } from "./slices/hexVersionMPSlice";
import { lockReducer } from "./slices/lockSlice.js";
import {
  logicViewReducer,
  setActiveGridElementIds,
} from "./slices/logicViewSlice";
import { mapDatasetReducer } from "./slices/mapDatasetSlice";
import { mpDebuggerReducer } from "./slices/mpDebuggerSlice.js";
import { orgGuideFilesMPReducer } from "./slices/orgGuideFilesMPSlice.js";
import { outputContentReducer } from "./slices/outputContentSlice.js";
import { projectGraphV3Reducer } from "./slices/projectGraphV3Slice";
import { reviewRequestMPReducer } from "./slices/reviewRequestMPSlice.js";
import { savedViewDefaultAppSessionReducer } from "./slices/savedViewDefaultAppSessionSlice.js";
import { schemaTreeBrowserReducer } from "./slices/schemaTreeBrowserSlice";
import { semanticProjectVersionMPReducer } from "./slices/semanticProjectVersionMPSlice.js";
import { semanticProjectWorkbenchReducer } from "./slices/semanticProjectWorkbenchSlice.js";
import { threadInlineReducer } from "./slices/threadInlineSlice.js";
import { uiReducer } from "./slices/uiSlice";
import { getSubscribeOnChangeEnhancer } from "./subscribeOnChange";

interface StoreOptions {
  getHexFlag?: GetHexFlagFn;
  disableSagas?: boolean;
  client: ApolloClient<unknown>;
}

export const createStore = (
  orgId: OrgId,
  {
    client,
    disableSagas = false,
    getHexFlag = (flag) => DEFAULT_FLAGS[flag],
  }: StoreOptions,
) => {
  const sagaMiddleware = createSagaMiddleware();
  const store = configureStore({
    reducer: {
      appSessionEvent: appSessionEventSliceReducer,
      appSessionMP: appSessionMPReducer,
      savedViewDefaultAppSession: savedViewDefaultAppSessionReducer,
      appView: appViewReducer,
      browserStorage: browserStorageReducer,
      hexMP: hexMPReducer,
      hexVersionMP: hexVersionMPReducer,
      currentUser: currentUserReducer,
      dataApp: dataAppReducer,
      dataAppFileMP: dataAppFileMPReducer,
      logicView: logicViewReducer,
      cellGraph: cellGraphReducer,
      outputContent: outputContentReducer,
      projectGraphV3: projectGraphV3Reducer,
      mapDataset: mapDatasetReducer,
      schemaTreeBrowser: schemaTreeBrowserReducer,
      dataBrowser: dataBrowserTreeReducer,
      reviewRequestMP: reviewRequestMPReducer,
      mpDebugger: mpDebuggerReducer,
      lock: lockReducer,
      hexSelection: hexSelectionReducer,
      ui: uiReducer,
      semanticProjectVersionMP: semanticProjectVersionMPReducer,
      semanticProjectWorkbench: semanticProjectWorkbenchReducer,
      agentChat: agentChatReducer,
      projectChat: projectChatReducer,
      agentLogicView: agentLogicViewReducer,
      contextWorkbench: contextWorkbenchReducer,
      orgGuideFilesMP: orgGuideFilesMPReducer,
      threadInline: threadInlineReducer,
    },
    enhancers: (getDefaultEnhancers) =>
      getDefaultEnhancers().concat(getSubscribeOnChangeEnhancer()),
    middleware: (getDefaultMiddleware) => {
      const defaults = getDefaultMiddleware({
        thunk: false,
        serializableCheck: {
          ignoredActions: [
            // this action takes a callback for reading current state
            // maybe we should just read from state where we dispatch them instead?
            setActiveGridElementIds.type,
          ],
          ignoredPaths: [
            // project graph uses maps and sets internally
            "projectGraphV3",
          ],
        },
      });
      if (disableSagas) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- rtk types really don't like returning a potentially 'empty' middleware array
        return defaults as any;
      } else {
        return defaults.concat(sagaMiddleware);
      }
    },
    devTools: {
      serialize: {
        options: {
          map: true,
        },
      },
    },
  });
  if (!disableSagas) {
    sagaMiddleware.run(rootSaga, orgId, { getHexFlag, client });
  }
  return store;
};

export type AppStore = ReturnType<typeof createStore>;
export type RootState = ReturnType<AppStore["getState"]>;
export type AppDispatch = AppStore["dispatch"];
export type AppAction = Parameters<AppDispatch>[0];
